import type { UserSettings } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { useWorkspaceDevices } from "@api/generated/workspace/workspace";
import { allItems } from "@app/nav";
import { useSaveSetting, useSeason, useSettings } from "@app/workspace";
import { LANGUAGES } from "@i18n";
import { Avatar, SegmentedControl, Select, TextInput } from "@mantine/core";
import { ShortcutList } from "@shell/Dialogs";
import { createFileRoute } from "@tanstack/react-router";
import { ago } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { SaveState } from "@ui/SaveState";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/system/settings")({ component: SettingsPage });

const ONLINE_MINUTES = 60;
const SAVED_SHOWN = 2500; // ms a field says "Mentve" after its save

type Key = keyof UserSettings;
type FieldState = "saving" | "saved";

/** Save a setting and follow each field's own save: "Mentés…" while it is on its way, "Mentve" for a moment after
 *  (a failure says why in a toast and the field goes back). */
function useFieldSave() {
  const save = useSaveSetting();
  const [state, setState] = useState<Partial<Record<Key, FieldState>>>({});
  const timers = useRef(new Map<Key, number>());
  const set = (key: Key, next?: FieldState) => {
    window.clearTimeout(timers.current.get(key));
    setState((s) => ({ ...s, [key]: next }));
  };
  const put = <K extends Key>(key: K, value: UserSettings[K]) => {
    set(key, "saving");
    save(key, value, {
      onSuccess: () => {
        set(key, "saved");
        timers.current.set(
          key,
          window.setTimeout(() => set(key), SAVED_SHOWN),
        );
      },
      onError: () => set(key),
    });
  };
  return { put, state };
}

/** A field's label with its save state beside it. */
function Label({ text, state }: { text: string; state?: FieldState }) {
  const { t } = useTranslation();
  return (
    <span className="field-label">
      {text}
      <SaveState saving={state === "saving"} saved={state === "saved" ? t("settings.saved") : null} />
    </span>
  );
}

function Choice<T extends string>({
  label,
  state,
  value,
  options,
  onChange,
  text,
}: {
  label: string;
  state?: FieldState;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  text: (v: T) => string;
}) {
  return (
    <div>
      <span style={{ display: "block", fontSize: "var(--b-text-small)", fontWeight: 500, color: "var(--b-ink-3)", marginBottom: 4 }}>
        <Label text={label} state={state} />
      </span>
      <SegmentedControl
        aria-label={label}
        color="breeder"
        size="sm"
        value={value}
        onChange={(v) => onChange(v as T)}
        data={options.map((o) => ({ value: o, label: text(o) }))}
      />
    </div>
  );
}

/** A text setting saved when the field loses focus. */
function SettingText({
  id,
  label,
  state,
  value,
  onSave,
}: {
  id: string;
  label: string;
  state?: FieldState;
  value: string;
  onSave: (v: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);
  return (
    <TextInput
      id={id}
      label={<Label text={label} state={state} />}
      value={text}
      onChange={(e) => setText(e.currentTarget.value.toUpperCase())}
      onBlur={() => text !== value && onSave(text.trim())}
      styles={{ input: { fontFamily: "var(--b-mono)" } }}
    />
  );
}

function SettingsPage() {
  const { t } = useTranslation();
  const settings = useSettings();
  const { put: save, state } = useFieldSave();
  const { years } = useSeason();
  const me = useUserMe();
  const devices = useWorkspaceDevices();
  if (!settings) return null;

  return (
    <div className="page">
      <PageHeader title={t("settings.title")} actions={<span className="sm mu">{t("settings.subtitle")}</span>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <div className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2 className="h2">{t("settings.defaults")}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
              <SettingText
                id="b1"
                label={t("settings.site")}
                state={state.default_th}
                value={settings.default_th}
                onSave={(v) => save("default_th", v)}
              />
              <SettingText
                id="b2"
                label={t("settings.pgm")}
                state={state.default_pgm}
                value={settings.default_pgm}
                onSave={(v) => save("default_pgm", v)}
              />
              <SettingText
                id="b3"
                label={t("settings.gh")}
                state={state.default_gh}
                value={settings.default_gh}
                onSave={(v) => save("default_gh", v)}
              />
            </div>
            <Select
              label={<Label text={t("settings.startYear")} state={state.season} />}
              data={[
                { value: "", label: t("settings.latestYear", { ev: years[0] ?? "" }) },
                ...years.map((y) => ({ value: String(y), label: String(y) })),
              ]}
              value={settings.season != null ? String(settings.season) : ""}
              onChange={(v) => save("season", v ? Number(v) : null)}
            />
            <Select
              label={<Label text={t("settings.startPage")} state={state.home_page} />}
              data={allItems().map(({ section, item }) => ({
                value: item.to,
                label: `${t(`nav.${section.key}.title`)} / ${t(`nav.${section.key}.${item.id}`)}`,
              }))}
              value={settings.home_page}
              onChange={(v) => v && save("home_page", v)}
            />
          </section>
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2 className="h2">{t("settings.appearance")}</h2>
            <Choice
              label={t("settings.language")}
              state={state.language}
              value={settings.language}
              options={LANGUAGES}
              onChange={(v) => save("language", v)}
              text={(v) => t(`settings.languages.${v}`)}
            />
            <Choice
              label={t("settings.theme")}
              state={state.theme}
              value={settings.theme}
              options={["light", "dark", "auto"] as const}
              onChange={(v) => save("theme", v)}
              text={(v) => t(`settings.themes.${v}`)}
            />
            <Choice
              label={t("settings.density")}
              state={state.density}
              value={settings.density}
              options={["comfortable", "compact"] as const}
              onChange={(v) => save("density", v)}
              text={(v) => t(`settings.densities.${v}`)}
            />
            <Choice
              label={t("settings.fontSize")}
              state={state.font_size}
              value={settings.font_size}
              options={["small", "normal", "large"] as const}
              onChange={(v) => save("font_size", v)}
              text={(v) => t(`settings.fontSizes.${v}`)}
            />
          </section>
        </div>

        <section className="card pad stack" style={{ gap: 4 }} id="shortcuts">
          <h2 className="h2" style={{ marginBottom: 6 }}>
            {t("shortcuts.title")}
          </h2>
          <ShortcutList />
        </section>

        <div className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 4 }}>
            <h2 className="h2" style={{ marginBottom: 6 }}>
              {t("settings.devices")}
            </h2>
            {devices.data?.length === 0 && <span className="sm mu">{t("settings.noDevices")}</span>}
            {(devices.data ?? []).map((d) => {
              const online = Date.now() - new Date(d.last_seen).getTime() < ONLINE_MINUTES * 60_000;
              return (
                <div
                  key={d.client_id}
                  className="hstack"
                  style={{ gap: 10, padding: "8px 0", borderBottom: "1px solid var(--b-row)", fontSize: "var(--b-text-body)" }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: online ? "#2f8a44" : "#b8bcb3" }} />
                  <span className="grow">
                    {d.client_id}
                    {d.last_user && <span className="mu"> · {d.last_user}</span>}
                  </span>
                  <span className="sm mu">
                    {ago(d.last_seen)} · {t("settings.messageCount", { count: d.messages })}
                  </span>
                </div>
              );
            })}
            <span className="sm mu" style={{ marginTop: 6 }}>
              {t("settings.devicesDescription")}
            </span>
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2 className="h2">{t("settings.account")}</h2>
            {me.data && (
              <>
                <div className="hstack" style={{ gap: 10 }}>
                  <Avatar name={me.data.full_name ?? me.data.username} color="breeder" variant="filled" radius="xl" />
                  <div className="stack">
                    <b style={{ fontWeight: 600 }}>{me.data.full_name ?? me.data.username}</b>
                    <span className="sm mu">
                      {me.data.username}
                      {me.data.email ? ` · ${me.data.email}` : ""}
                    </span>
                  </div>
                </div>
                <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
                  {me.data.roles.map((r) => (
                    <span key={r} className={`chip ${r === "admin" ? "b" : ""}`}>
                      {t(`roles.${r}`, { defaultValue: r })}
                    </span>
                  ))}
                </div>
              </>
            )}
            <span className="sm mu">{t("settings.accountDescription")}</span>
          </section>
        </div>
      </div>
    </div>
  );
}
